import type { DocumentExtension } from '@app/contracts';
import {
  ClipboardList,
  FileText,
  FileWarning,
  Globe,
  Layers,
  ScrollText,
  Settings2,
  Shield,
  X,
} from 'lucide-react';
import type { KeyboardEvent } from 'react';
import { useState } from 'react';
import { useCreateDocument } from '../hooks/useDocuments.js';

interface ToolOption {
  id: DocumentExtension;
  title: string;
  description: string;
  icon: React.ReactNode;
  iconBg: string;
}

const TOOLS: ToolOption[] = [
  {
    id: 'swot',
    title: 'Matrice SWOT',
    description: 'Analysez les forces, faiblesses, opportunités et menaces.',
    icon: <Layers className="h-6 w-6 text-white" />,
    iconBg: 'bg-blue-500',
  },
  {
    id: 'pestel',
    title: 'Matrice PESTEL',
    description: "Évaluez l'environnement macro-économique en 6 dimensions.",
    icon: <Globe className="h-6 w-6 text-white" />,
    iconBg: 'bg-green-500',
  },
  {
    id: 'needsWait',
    title: 'Parties intéressées',
    description: 'Registre ISO 9001:4.2 des parties intéressées.',
    icon: <FileText className="h-6 w-6 text-white" />,
    iconBg: 'bg-orange-500',
  },
  {
    id: 'paOperational',
    title: 'PA Opérationnel',
    description: "Suivi des actions préventives et d'amélioration.",
    icon: <Settings2 className="h-6 w-6 text-white" />,
    iconBg: 'bg-purple-500',
  },
  {
    id: 'auditPlanning',
    title: "Plan d'audit",
    description: 'Programme et calendrier des audits internes.',
    icon: <ClipboardList className="h-6 w-6 text-white" />,
    iconBg: 'bg-slate-600',
  },
  {
    id: 'auditReport',
    title: "Rapport d'audit",
    description: 'Compte rendu et conclusions des audits.',
    icon: <ScrollText className="h-6 w-6 text-white" />,
    iconBg: 'bg-indigo-600',
  },
  {
    id: 'nonConformity',
    title: 'Non-conformité',
    description: 'Constats, causes et actions correctives.',
    icon: <FileWarning className="h-6 w-6 text-white" />,
    iconBg: 'bg-red-600',
  },
  {
    id: 'managementReview',
    title: 'Revue de direction',
    description: 'Revue du système de management : décisions et actions.',
    icon: <Shield className="h-6 w-6 text-white" />,
    iconBg: 'bg-amber-600',
  },
];

interface Props {
  onClose: () => void;
}

export function CreateDocumentModal({ onClose }: Props) {
  const [selectedTool, setSelectedTool] = useState<ToolOption | null>(null);
  const [name, setName] = useState('');
  const [strictValidation, setStrictValidation] = useState(false);
  const [error, setError] = useState('');

  const createMutation = useCreateDocument();

  const handleSelectTool = (tool: ToolOption) => {
    setSelectedTool(tool);
    setName(`${tool.title} — ${new Date().toLocaleDateString('fr-FR')}`);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTool) return;
    if (!name.trim()) {
      setError('Le nom est obligatoire.');
      return;
    }
    try {
      await createMutation.mutateAsync({
        name: name.trim(),
        extension: selectedTool.id,
        strictValidation,
      });
      onClose();
    } catch {
      setError('Erreur lors de la création du document.');
    }
  };

  return (
    <>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: overlay ; fermeture explicite au clic ou via les boutons */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        onClick={onClose}
      >
        <div
          className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e: KeyboardEvent) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Créer un document</h2>
              <p className="text-sm text-slate-500">Choisissez un type d'outil stratégique</p>
            </div>
            <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          {!selectedTool ? (
            /* Tool selection */
            <div className="grid grid-cols-2 gap-4 p-6">
              {TOOLS.map((tool) => (
                <button
                  key={tool.id}
                  type="button"
                  onClick={() => handleSelectTool(tool)}
                  className="flex flex-col items-start gap-3 rounded-xl border border-slate-200 p-4 text-left transition-all hover:border-emerald-300 hover:bg-emerald-50 hover:shadow-sm"
                >
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-xl ${tool.iconBg}`}
                  >
                    {tool.icon}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{tool.title}</p>
                    <p className="mt-0.5 text-sm text-slate-500">{tool.description}</p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            /* Configuration form */
            <form onSubmit={handleSubmit} className="p-6">
              <div className="mb-4 flex items-center gap-3 rounded-xl bg-slate-50 p-3">
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${selectedTool.iconBg}`}
                >
                  {selectedTool.icon}
                </div>
                <div>
                  <p className="font-medium text-slate-900">{selectedTool.title}</p>
                  <button
                    type="button"
                    onClick={() => setSelectedTool(null)}
                    className="text-xs text-slate-400 hover:text-slate-600 underline"
                  >
                    Changer
                  </button>
                </div>
              </div>

              <label className="mb-4 flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                Nom du document
                <input
                  type="text"
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-normal outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setError('');
                  }}
                  placeholder={`Ex: ${selectedTool.title} — Projet X`}
                />
              </label>

              <div className="mb-6 flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-slate-400" />
                  <div>
                    <p className="text-sm font-medium text-slate-700">Validation stricte</p>
                    <p className="text-xs text-slate-400">
                      Imposer des règles de validation renforcées
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={strictValidation}
                  onClick={() => setStrictValidation((v) => !v)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${strictValidation ? 'bg-emerald-500' : 'bg-slate-200'}`}
                >
                  <span
                    className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${strictValidation ? 'translate-x-6' : 'translate-x-1'}`}
                  />
                </button>
              </div>

              {error && <p className="mb-3 text-sm text-red-500">{error}</p>}

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Création…' : 'Créer'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
